import React, { useState, useEffect, useRef } from "react";
import { apiErrorMessage } from "../lib/apiError";
import { useNavigate } from "../i18n/router";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/use-toast";
import { MessageCircle, Send, Loader2, ArrowLeft, Check, CheckCheck, Circle, Archive, ArchiveRestore, Inbox } from "lucide-react";
import { formatChatTime } from "../lib/time";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function StatusIcon({ status, isFromMe }) {
  if (!isFromMe) return null;
  if (status === "read") return <CheckCheck className="h-3.5 w-3.5" style={{ color: "var(--eunoia-accent)" }} />;
  if (status === "delivered") return <CheckCheck className="h-3.5 w-3.5" style={{ color: "var(--eunoia-soft)" }} />;
  return <Check className="h-3.5 w-3.5" style={{ color: "var(--eunoia-soft)" }} />;
}

function ThreadList({ threads, activeThread, onSelect, onArchive, onRestore, tab }) {
  if (threads.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <MessageCircle className="h-16 w-16 mb-4" style={{ color: "var(--eunoia-whisper)" }} />
        <h3 className="eunoia-title-small mb-2">
          {tab === "archive" ? "Архив пуст" : "Нет сообщений"}
        </h3>
        <p className="eunoia-caption max-w-xs">
          {tab === "archive"
            ? "Здесь будут переписки, которые вы заархивировали"
            : "Начните общение — напишите организатору через его профиль"}
        </p>
      </div>
    );
  }

  return (
    <div className="divide-y" style={{ borderColor: "var(--eunoia-whisper)" }}>
      {threads.map((t) => (
        <div
          key={t.threadId}
          role="button"
          tabIndex={0}
          onClick={() => onSelect(t)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(t); } }}
          data-testid={`thread-${t.threadId}`}
          className={`group relative w-full text-left p-4 hover:bg-black/[0.02] transition-colors cursor-pointer ${activeThread?.threadId === t.threadId ? 'bg-black/[0.03]' : ''}`}
        >
          <div className="flex items-start gap-3">
            {/* Avatar */}
            <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center"
              style={{ backgroundColor: "var(--eunoia-accent-whisper)" }}>
              <span className="text-sm font-semibold" style={{ color: "var(--eunoia-accent)" }}>
                {(t.otherName || "?")[0]}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className={`text-sm truncate ${t.unreadCount > 0 ? 'font-bold' : 'font-medium'}`} style={{ color: "var(--eunoia-deep)" }}>
                  {t.otherName}
                </span>
                <span className="text-[10px] flex-shrink-0" style={{ color: "var(--eunoia-soft)" }}>
                  {formatTime(t.lastMessageAt)}
                </span>
              </div>
              <p className="text-xs font-medium truncate mt-0.5" style={{ color: t.unreadCount > 0 ? "var(--eunoia-deep)" : "var(--eunoia-soft)" }}>
                {t.subject}
              </p>
              <p className="text-xs truncate mt-0.5" style={{ color: "var(--eunoia-soft)" }}>
                {t.isLastFromMe ? "Вы: " : ""}{t.lastMessage}
              </p>
            </div>
            {t.unreadCount > 0 && (
              <span className="w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white flex-shrink-0"
                style={{ backgroundColor: "var(--eunoia-accent)" }}>{t.unreadCount}</span>
            )}
          </div>

          {/* Hover action: archive (active tab) or restore (archive tab) */}
          {tab === "archive" ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRestore?.(t.threadId); }}
              data-testid={`restore-thread-${t.threadId}`}
              title="Восстановить из архива"
              className="absolute top-3 right-3 p-1.5 rounded-full bg-white/80 hover:bg-white shadow-sm opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
            >
              <ArchiveRestore className="h-3.5 w-3.5" style={{ color: "var(--eunoia-accent)" }} />
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onArchive?.(t.threadId); }}
              data-testid={`archive-thread-${t.threadId}`}
              title="Архивировать"
              className="absolute top-3 right-3 p-1.5 rounded-full bg-white/80 hover:bg-white shadow-sm opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
            >
              <Archive className="h-3.5 w-3.5" style={{ color: "var(--eunoia-soft)" }} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function ConversationView({ thread, user, onBack, onMessageSent }) {
  const [messages, setMessages] = useState([]);
  const [threadMeta, setThreadMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!thread) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/messages/thread/${thread.threadId}`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          // New shape: { thread, messages }
          if (data && Array.isArray(data.messages)) {
            setMessages(data.messages);
            setThreadMeta(data.thread || null);
          } else if (Array.isArray(data)) {
            // Backward-compat: bare array (older deploys)
            setMessages(data);
            setThreadMeta(null);
          }
        }
      } catch (e) { console.error(e); }
      finally {
        setLoading(false);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    })();
  }, [thread]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const handleReply = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const text = replyText.trim();
    if (!text || sending) return;
    if (!thread?.threadId) {
      console.error("[reply] missing threadId", thread);
      toast({ title: "Ошибка", description: "Переписка не выбрана. Попробуйте снова.", variant: "destructive" });
      return;
    }

    setSending(true);
    console.log("[reply] sending", { threadId: thread.threadId, length: text.length });

    try {
      const res = await fetch(`${BACKEND_URL}/api/messages/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ threadId: thread.threadId, body: text }),
      });

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        const newMsg = {
          id: data.id || `tmp-${Date.now()}`,
          threadId: thread.threadId,
          fromUserId: user.id,
          fromName: user.displayName,
          toUserId: thread.otherUserId,
          toName: thread.otherName,
          subject: thread.subject,
          body: text,
          status: "delivered",
          createdAt: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, newMsg]);
        setReplyText("");
        if (onMessageSent) onMessageSent();
        setTimeout(() => inputRef.current?.focus(), 30);
      } else if (res.status === 401) {
        toast({ title: "Требуется вход", description: "Сессия истекла — войдите снова.", variant: "destructive" });
      } else if (res.status === 404) {
        toast({ title: "Переписка не найдена", description: "Попробуйте открыть её заново.", variant: "destructive" });
      } else {
        const err = await res.json().catch(() => ({}));
        console.error("[reply] non-ok", res.status, err);
        toast({
          title: "Не удалось отправить сообщение",
          description: apiErrorMessage(err, "Попробуйте снова."),
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error("[reply] network/exception", err);
      toast({
        title: "Не удалось отправить сообщение",
        description: "Попробуйте снова.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b flex items-center gap-3" style={{ borderColor: "var(--eunoia-whisper)" }}>
        <button onClick={onBack} className="md:hidden p-1 rounded-full hover:bg-black/5" data-testid="back-to-threads">
          <ArrowLeft className="h-5 w-5" style={{ color: "var(--eunoia-soft)" }} />
        </button>
        <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center"
          style={{ backgroundColor: "var(--eunoia-accent-whisper)" }}>
          <span className="text-sm font-semibold" style={{ color: "var(--eunoia-accent)" }}>{(thread.otherName || "?")[0]}</span>
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate" style={{ color: "var(--eunoia-deep)" }}>{thread.otherName}</p>
          <p className="text-xs truncate" style={{ color: "var(--eunoia-soft)" }}>{thread.subject}</p>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 sm:px-4 py-4 min-h-0">
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--eunoia-accent)" }} /></div>
        ) : (() => {
          // Determine the organizer of this thread from EXPLICIT thread metadata.
          // Priority: 1) freshly-fetched thread meta, 2) parent thread row from /threads,
          // 3) last-resort first-message fallback (only for ancient threads that somehow
          //    have no backfilled metadata yet). The first two cover all real cases.
          const organizerId =
            threadMeta?.organizerId ||
            thread?.organizerId ||
            messages[0]?.toUserId; // safety net only

          return messages.map((msg, idx) => {
            const isFromOrganizer = !!organizerId && msg.fromUserId === organizerId;
            const isMe = msg.fromUserId === user.id; // only used for read-receipt status icon
            const prev = messages[idx - 1];
            const next = messages[idx + 1];
            const sameAsPrev = prev && prev.fromUserId === msg.fromUserId;
            const sameAsNext = next && next.fromUserId === msg.fromUserId;
            const isFirstInGroup = !sameAsPrev;
            const isLastInGroup = !sameAsNext;

            // Spacing: bigger gap between groups, tighter within a group
            const gapClass = idx === 0 ? "" : sameAsPrev ? "mt-1" : "mt-4";

            // Side & corner tail are driven by ROLE, not by isMe
            const alignRight = isFromOrganizer;
            const tailClass = isLastInGroup
              ? (alignRight ? "rounded-br-md" : "rounded-bl-md")
              : "";

            // Bubble appearance — role-based
            const organizerStyle = {
              backgroundColor: "var(--eunoia-accent)",
              color: "white",
              border: "none",
              boxShadow: "0 1px 2px rgba(189,162,122,0.18)",
            };
            const participantStyle = {
              backgroundColor: "white",
              color: "var(--eunoia-deep)",
              border: "1px solid var(--eunoia-whisper)",
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            };

            return (
              <div
                key={msg.id}
                className={`${gapClass} flex ${alignRight ? "justify-end" : "justify-start"}`}
                data-testid={`msg-bubble-${msg.id}`}
                data-role={isFromOrganizer ? "organizer" : "participant"}
              >
                <div className={`max-w-[85%] md:max-w-[68%] flex flex-col ${alignRight ? "items-end" : "items-start"}`}>
                  {/* Sender name — only on the first bubble of a participant's group */}
                  {isFirstInGroup && !isFromOrganizer && (
                    <span className="text-[11px] font-medium mb-1 px-1" style={{ color: "var(--eunoia-soft)" }}>
                      {msg.fromName}
                    </span>
                  )}

                  <div
                    className={`rounded-2xl px-4 py-2.5 ${tailClass}`}
                    style={isFromOrganizer ? organizerStyle : participantStyle}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{msg.body}</p>
                  </div>

                  {/* Timestamp + status — only on the last bubble of a group */}
                  {isLastInGroup && (
                    <div className={`flex items-center gap-1.5 mt-1 px-1 ${alignRight ? "flex-row-reverse" : ""}`}>
                      <span className="text-[10px]" style={{ color: "var(--eunoia-soft)" }}>
                        {formatTime(msg.createdAt)}
                      </span>
                      {/* Read-receipt only shows on messages the current viewer sent */}
                      <StatusIcon status={msg.status} isFromMe={isMe} />
                    </div>
                  )}
                </div>
              </div>
            );
          });
        })()}
      </div>

      {/* Reply input */}
      <form onSubmit={handleReply} className="p-3 border-t flex gap-2 flex-shrink-0" style={{ borderColor: "var(--eunoia-whisper)", backgroundColor: "white" }}>
        <textarea
          ref={inputRef}
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(e); } }}
          className="eunoia-input flex-1 min-h-[44px] max-h-[120px] resize-none py-3 text-sm"
          placeholder="Написать ответ..."
          rows={1}
          maxLength={5000}
          data-testid="reply-input"
        />
        <button
          type="submit"
          disabled={!replyText.trim() || sending}
          className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-all disabled:opacity-40"
          style={{ backgroundColor: "var(--eunoia-accent)" }}
          data-testid="reply-send-btn"
        >
          {sending ? <Loader2 className="h-4 w-4 text-white animate-spin" /> : <Send className="h-4 w-4 text-white" />}
        </button>
      </form>
    </div>
  );
}

function formatTime(dt) {
  return formatChatTime(dt);
}

const MessagesPage = () => {
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeThread, setActiveThread] = useState(null);
  const [tab, setTab] = useState("active"); // "active" | "archive"

  useEffect(() => {
    if (!authLoading && !user) navigate("/login?redirect=/messages");
  }, [user, authLoading, navigate]);

  const fetchThreads = async (whichTab = tab) => {
    if (!user) return;
    setLoading(true);
    try {
      const url = whichTab === "archive"
        ? `${BACKEND_URL}/api/messages/threads?archived=true`
        : `${BACKEND_URL}/api/messages/threads`;
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) setThreads(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchThreads(tab); }, [user, tab]);

  const handleSelectThread = (t) => {
    setActiveThread(t);
    // Clear unread in local state
    setThreads((prev) => prev.map((th) => th.threadId === t.threadId ? { ...th, unreadCount: 0 } : th));
  };

  const handleArchive = async (threadId) => {
    // Optimistic: remove from active list
    const snapshot = threads;
    setThreads((prev) => prev.filter((t) => t.threadId !== threadId));
    if (activeThread?.threadId === threadId) setActiveThread(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/messages/thread/${threadId}/archive`, {
        method: "POST", credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast({ title: "Переписка перемещена в архив" });
    } catch (e) {
      console.error("[archive] failed", e);
      setThreads(snapshot); // rollback
      toast({ title: "Не удалось архивировать", description: "Попробуйте снова.", variant: "destructive" });
    }
  };

  const handleRestore = async (threadId) => {
    const snapshot = threads;
    setThreads((prev) => prev.filter((t) => t.threadId !== threadId));
    if (activeThread?.threadId === threadId) setActiveThread(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/messages/thread/${threadId}/restore`, {
        method: "POST", credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast({ title: "Переписка восстановлена" });
    } catch (e) {
      console.error("[restore] failed", e);
      setThreads(snapshot);
      toast({ title: "Не удалось восстановить", description: "Попробуйте снова.", variant: "destructive" });
    }
  };

  if (authLoading || !user) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--eunoia-accent)" }} /></div>;
  }

  return (
    <div className="min-h-screen eunoia-space-lg px-4 pb-4" data-testid="messages-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <MessageCircle className="h-7 w-7" style={{ color: "var(--eunoia-accent)" }} />
          <h1 className="eunoia-title-large">Сообщения</h1>
        </div>

        <div className="eunoia-surface overflow-hidden flex" style={{ height: "calc(100vh - 180px)" }}>
          {/* Thread List - hide on mobile when conversation is open */}
          <div className={`w-full md:w-80 lg:w-96 border-r flex-shrink-0 flex flex-col ${activeThread ? 'hidden md:flex' : ''}`}
            style={{ borderColor: "var(--eunoia-whisper)" }}>
            {/* Tabs */}
            <div className="flex border-b flex-shrink-0" style={{ borderColor: "var(--eunoia-whisper)" }}>
              {[
                { key: "active",  label: "Активные", icon: Inbox },
                { key: "archive", label: "Архив",    icon: Archive },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => { setActiveThread(null); setTab(t.key); }}
                  data-testid={`tab-${t.key}`}
                  className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors relative"
                  style={{
                    color: tab === t.key ? "var(--eunoia-accent)" : "var(--eunoia-soft)",
                  }}
                >
                  <t.icon className="h-4 w-4" />
                  {t.label}
                  {tab === t.key && (
                    <span className="absolute bottom-0 left-3 right-3 h-[2px] rounded-full" style={{ backgroundColor: "var(--eunoia-accent)" }} />
                  )}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--eunoia-accent)" }} /></div>
              ) : (
                <ThreadList
                  threads={threads}
                  activeThread={activeThread}
                  onSelect={handleSelectThread}
                  onArchive={handleArchive}
                  onRestore={handleRestore}
                  tab={tab}
                />
              )}
            </div>
          </div>

          {/* Conversation - show on mobile only when thread selected */}
          <div className={`flex-1 flex flex-col ${!activeThread ? 'hidden md:flex' : ''}`}>
            {activeThread ? (
              <ConversationView
                thread={activeThread}
                user={user}
                onBack={() => setActiveThread(null)}
                onMessageSent={() => fetchThreads(tab)}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center p-8 text-center">
                <div>
                  <MessageCircle className="h-16 w-16 mx-auto mb-4" style={{ color: "var(--eunoia-whisper)" }} />
                  <p className="eunoia-body">Выберите переписку</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
